import React, { useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import { Typography, Tooltip, IconButton } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import dayjs from "dayjs";
import ProGrid from "../../../Components/ProGrid/v2";
import useColumn from "../../../../utils/customHooks/useColumns";
import usePageState from "../../../../utils/customHooks/usePageState";
import { STAUTES } from "../../../../utils/constant";
import { getApi } from "../../../../utils/services";
import { GET_PREVAILING_WAGE_UNDER_PAYMENT_OVERVIEW_SUMMARY } from "../../../../utils/services/apiPath";
import { createODataQuery } from "../../../../utils/services/oData";
import { errorToast } from "../../../../utils/toastHelper";
import "./UnderPaymentListTable.scss";

const getRowId = row => `${row.employeeGuid}-${row.projectGuid}-${row.payPeriodEndingDate}`;

const parseLaborClassifications = value =>
  (value ?? "")
    .split(",")
    .map(part => part.trim())
    .filter(Boolean);

const renderTwoLineHeader = (title, subtitle) => () => (
  <div className="underpayment-list-table-header-cell">
    <span className="underpayment-list-table-header-cell-title">{title}</span>
    <span className="underpayment-list-table-header-cell-subtitle">{subtitle}</span>
  </div>
);

const BACK_PAYMENT_TOOLTIP = "For more information, please contact your CSM or your tax advisor for additional support.";

const renderInfoHeader = title => () => (
  <span className="underpayment-list-table-header-cell-info">
    {title}
    <Tooltip title={BACK_PAYMENT_TOOLTIP}>
      <IconButton size="small" className="underpayment-list-table-info-icon">
        <InfoOutlinedIcon fontSize="inherit" />
      </IconButton>
    </Tooltip>
  </span>
);

const COLUMN_LABELS = groupBy => ({
  employee: "Employee",
  secondary: groupBy === "payPeriod" ? "Project" : "Pay Period",
  laborClassification: "Labor Classification",
  requiredWage: "Required / Reported Wage",
  requiredFringe: "Required / Reported Fringe",
  backPayment: "Back Payment"
});

export default function UnderPaymentListTable({ groupBy, activeGroup, onSelectRecord, formatCurrency, isGroupsLoading, projectFilter }) {
  const {
    currentPage,
    pageSize,
    data: rows,
    rowCount,
    status,
    sortModel,
    setPageState,
    setPagination,
    setStatus,
    setSortModel
  } = usePageState({ pageSize: 15 });

  const isLoading = status === STAUTES.LOADING;
  const filterValue = activeGroup ? (groupBy === "payPeriod" ? activeGroup.payPeriodEndingDate : activeGroup.projectGuid) : null;
  const activeGroupKey = activeGroup?.key ?? null;

  const getBackPaymentRows = useCallback(async () => {
    if (!activeGroup) {
      setPageState(prev => ({ ...prev, data: [], rowCount: 0 }));
      return;
    }

    setStatus(STAUTES.LOADING);
    try {
      const groupExpression =
        groupBy === "payPeriod" ? `payPeriodEndingDate eq ${filterValue}` : `projectGuid eq ${filterValue}`;
      const filterExpression = [groupExpression, projectFilter].filter(Boolean).join(" and ");

      const odataQuery = createODataQuery([], [], filterExpression, "", sortModel, pageSize, currentPage, true);

      const { data, totalRecords, error } = await getApi(
        `${GET_PREVAILING_WAGE_UNDER_PAYMENT_OVERVIEW_SUMMARY}${odataQuery}`
      );

      setStatus(STAUTES.IDLE);
      if (error) return errorToast(error?.message || error || "Unable to load prevailing wage underpayments.");
      setPageState(prev => ({ ...prev, data: data ?? [], rowCount: totalRecords ?? 0 }));
    } catch (err) {
      setStatus(STAUTES.IDLE);
      errorToast(err?.message || "Unable to load prevailing wage underpayments.");
    }
  }, [activeGroupKey, groupBy, filterValue, projectFilter, sortModel, pageSize, currentPage, setPageState, setStatus]);

  useEffect(() => {
    getBackPaymentRows();
  }, [getBackPaymentRows]);

  const secondaryField = groupBy === "payPeriod" ? "projectName" : "payPeriodEndingDate";
  const labels = COLUMN_LABELS(groupBy);

  const columns = useColumn(
    [
      {
        field: "employeeName",
        headerName: labels.employee,
        flex: 1,
        minWidth: 160,
        renderCell: params => (
          <button
            type="button"
            className="underpayment-list-table-employee-link"
            onClick={() => onSelectRecord(params.row)}
          >
            {params.value}
          </button>
        )
      },
      {
        field: secondaryField,
        headerName: labels.secondary,
        flex: 1,
        minWidth: 150,
        ...(groupBy === "project" && {
          renderCell: params => <span>{params.value ? dayjs(params.value).format("MM/DD/YYYY") : ""}</span>
        })
      },
      {
        field: "laborClassification",
        headerName: labels.laborClassification,
        flex: 1.1,
        minWidth: 180,
        renderCell: params => {
          const classifications = parseLaborClassifications(params.value);
          if (classifications.length > 1) {
            return (
              <div className="underpayment-list-table-classification-cell">
                <span className="underpayment-list-table-classification-count">
                  {classifications.length} classifications
                </span>
                <span className="underpayment-list-table-classification-list">{classifications.join(", ")}</span>
              </div>
            );
          }
          return <span>{params.value}</span>;
        }
      },
      {
        field: "requiredWageAmount",
        flex: 1.3,
        minWidth: 220,
        renderHeader: renderTwoLineHeader(labels.requiredWage, "(Total of both labor classifications)"),
        renderCell: params => (
          <span>
            {formatCurrency(params.row.requiredWageAmount)} / {formatCurrency(params.row.reportedWageAmount)}
          </span>
        )
      },
      {
        field: "requiredFringeAmount",
        flex: 1.3,
        minWidth: 220,
        renderHeader: renderTwoLineHeader(labels.requiredFringe, "(Total of both labor classifications)"),
        renderCell: params => (
          <span>
            {formatCurrency(params.row.requiredFringeAmount)} / {formatCurrency(params.row.reportedFringeAmount)}
          </span>
        )
      },
      {
        field: "backPaymentAmount",
        flex: 0.8,
        minWidth: 140,
        align: "right",
        renderHeader: renderInfoHeader(labels.backPayment),
        renderCell: params => (
          <span className="underpayment-list-table-cell--alert">{formatCurrency(params.value)}</span>
        )
      }
    ],
    true
  );

  return (
    <div className="underpayment-list-table">
      <div className="underpayment-list-table-header">
        <Typography className="underpayment-list-table-group-label">
          {activeGroup ? activeGroup.label : isGroupsLoading ? "Loading…" : "No Data"}
        </Typography>
        {activeGroup && (
          <Typography className="underpayment-list-table-summary">
            {rowCount.toLocaleString("en-US")} records &middot; {formatCurrency(activeGroup.amount)} back payment (interest
            not included)
            <Tooltip title="For more information, please contact your CSM or your tax advisor for additional support.">
              <IconButton size="small" className="underpayment-list-table-info-icon">
                <InfoOutlinedIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          </Typography>
        )}
      </div>

      <div className="underpayment-list-table-grid-wrapper">
        <ProGrid
          hideSearch={true}
          hideAddButton={true}
          hideExport={true}
          columns={columns}
          rows={rows}
          loading={isLoading || isGroupsLoading}
          options={{
            getRowId,
            rowCount,
            paginationMode: "server",
            paginationModel: { pageSize, page: currentPage },
            onPaginationModelChange: setPagination,
            sortingMode: "server",
            onSortModelChange: setSortModel,
            columnHeaderHeight: 60,
            rowHeight: 52,
            disableColumnMenu: true
          }}
        />
      </div>
    </div>
  );
}

UnderPaymentListTable.propTypes = {
  groupBy: PropTypes.oneOf(["payPeriod", "project"]).isRequired,
  activeGroup: PropTypes.shape({
    key: PropTypes.string,
    label: PropTypes.string,
    amount: PropTypes.number,
    payPeriodEndingDate: PropTypes.string,
    projectGuid: PropTypes.string
  }),
  onSelectRecord: PropTypes.func.isRequired,
  formatCurrency: PropTypes.func.isRequired,
  isGroupsLoading: PropTypes.bool,
  projectFilter: PropTypes.string
};

UnderPaymentListTable.defaultProps = {
  activeGroup: null,
  isGroupsLoading: false,
  projectFilter: ""
};